import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { forumApi } from '../../api/forum';
import { UserContext } from '../../context/UserContext';
import { isAdmin, isModerator } from '../../admin/utils/roles';
import { getAccessDeniedMessage } from '../../utils/moderationErrors';
import { renderMarkdownLite } from '../../utils/markdownLite';
import { formatRelativeTime } from '../../utils/relativeTime';
import { highlightText } from '../../utils/highlightText';
import { getUnreadCommentCount } from '../../utils/postReadState';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbModal from '../controls/HrnbModal';
import HrnbLoader from '../controls/HrnbLoader';
import TagBadge from './TagBadge';
import '../../css/components/PostCard.css';

function PostCard({ post, onDelete, onPinToggle, searchQuery = '', isSubscribedView = false }) {
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const [liked, setLiked] = useState(post.liked || false);
    const [likeCount, setLikeCount] = useState(post.likes || post.likeCount || 0);
    const [disliked, setDisliked] = useState(post.disliked || false);
    const [dislikeCount, setDislikeCount] = useState(post.dislikes || post.dislikeCount || 0);
    const [subscribed, setSubscribed] = useState(post.subscribed || false);
    const [subscribeLoading, setSubscribeLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    const [pinned, setPinned] = useState(post.status === 'PINNED');
    const [pinLoading, setPinLoading] = useState(false);

    const handleViewDetails = () => {
        // Сохраняем флаг, что переход на пост был с форума — для корректного "Назад"
        try {
            sessionStorage.setItem('hrnb_forum_from_forum', 'true');
        } catch (e) { /* ignore */ }
        navigate(`/forum/posts/${post.id}`);
    };

    const handleLike = async (e) => {
        e.stopPropagation();
        if (!currentUser) {
            return;
        }
        try {
            if (liked) {
                await forumApi.unlikePost(post.id);
                setLikeCount(prev => prev - 1);
                setLiked(false);
            } else {
                if (disliked) {
                    setDislikeCount(prev => prev - 1);
                    setDisliked(false);
                }
                await forumApi.likePost(post.id);
                setLikeCount(prev => prev + 1);
                setLiked(true);
            }
        } catch (err) {
            console.error('[PostCard] Like failed:', err.message);
        }
    };

    const handleDislike = async (e) => {
        e.stopPropagation();
        if (!currentUser) {
            return;
        }
        try {
            if (disliked) {
                await forumApi.undislikePost(post.id);
                setDislikeCount(prev => prev - 1);
                setDisliked(false);
            } else {
                if (liked) {
                    setLikeCount(prev => prev - 1);
                    setLiked(false);
                }
                await forumApi.dislikePost(post.id);
                setDislikeCount(prev => prev + 1);
                setDisliked(true);
            }
        } catch (err) {
            console.error('[PostCard] Dislike failed:', err.message);
        }
    };

    const handleSubscribe = async (e) => {
        e.stopPropagation();
        if (!currentUser) {
            return;
        }
        try {
            setSubscribeLoading(true);
            if (subscribed) {
                await forumApi.unsubscribeFromPost(post.id);
                setSubscribed(false);
            } else {
                await forumApi.subscribeToPost(post.id);
                setSubscribed(true);
            }
        } catch (err) {
            console.error('[PostCard] Subscribe failed:', err.message);
        } finally {
            setSubscribeLoading(false);
        }
    };

    const handleDeletePost = async () => {
        if (!currentUser) {
            return;
        }

        const authorProfileId = currentUser.profiles?.[0]?.id;
        const isAuthor = post.author?.id === authorProfileId || post.author?.userId === currentUser.id;
        const canDelete = isAuthor || isAdmin(currentUser) || isModerator(currentUser);

        if (!canDelete) {
            return;
        }

        try {
            setDeleting(true);
            await forumApi.deletePost(post.id);
            setDeleteModalOpen(false);
            if (onDelete) {
                onDelete(post.id);
            }
        } catch (err) {
            console.error('[PostCard] Failed to delete post:', err.message);
            setError(getAccessDeniedMessage(err) || err.response?.data?.message || 'Не удалось удалить пост');
        } finally {
            setDeleting(false);
        }
    };

    const canDeletePost = () => {
        if (!currentUser) return false;
        const authorProfileId = currentUser.profiles?.[0]?.id;
        const isAuthor = post.author?.id === authorProfileId || post.author?.userId === currentUser.id;
        return isAuthor || isAdmin(currentUser) || isModerator(currentUser);
    };

    const canPinPost = () => {
        if (!currentUser) return false;
        return isAdmin(currentUser) || isModerator(currentUser);
    };

    const handlePinToggle = async (e) => {
        e.stopPropagation();
        if (!canPinPost()) return;
        try {
            setPinLoading(true);
            const newStatus = pinned ? 'VISIBLE' : 'PINNED';
            await forumApi.updatePostStatus(post.id, newStatus);
            setPinned(!pinned);
            if (onPinToggle) {
                onPinToggle(post.id, !pinned);
            }
        } catch (err) {
            console.error('[PostCard] Failed to toggle pin:', err.message);
            setError(getAccessDeniedMessage(err) || 'Не удалось изменить закрепление поста');
        } finally {
            setPinLoading(false);
        }
    };

    const formatDate = (dateStr) => formatRelativeTime(dateStr);

    const {
        title,
        content,
        author,
        category,
        tags = [],
        createdAt,
        updatedAt,
        commentsCount = 0,
        commentCount,
        viewCount = 0,
        views,
    } = post;

    const displayViewCount = views ?? viewCount;
    const displayCommentsCount = commentCount ?? commentsCount;

    // Показываем updatedAt только если он ощутимо отличается от createdAt
    const isEdited = updatedAt && createdAt && (
        new Date(updatedAt).getTime() - new Date(createdAt).getTime() > 5000
    );

    // Непрочитанные комментарии (для подписанных постов)
    const isTracked = subscribed || isSubscribedView;
    const unreadCount = isTracked ? getUnreadCommentCount(post.id, displayCommentsCount) : 0;
    const hasUnread = unreadCount > 0;

    // Извлекаем изображения из вложений для миниатюры
    const BASE_URL = 'https://api.hrnb.ru/api';
    const imageAttachments = (post.attachments || []).filter(
        a => a.fileType === 'IMAGE' || a.contentType?.startsWith('image/')
    );
    const thumbnail = imageAttachments.length > 0 ? imageAttachments[0] : null;
    const extraImageCount = imageAttachments.length > 1 ? imageAttachments.length - 1 : 0;

    const renderContent = (text) => {
        const rendered = text.length > 150
            ? renderMarkdownLite(`${text.substring(0, 150)}...`)
            : renderMarkdownLite(text);

        if (!searchQuery || !searchQuery.trim()) return rendered;

        // При активном поиске подсвечиваем совпадения в текстовых узлах
        return rendered.flatMap((el, i) => {
            if (typeof el === 'string') {
                return highlightText(el, searchQuery);
            }
            return [el];
        });
    };

    const renderTitle = () => {
        if (!searchQuery || !searchQuery.trim()) return title;
        return highlightText(title, searchQuery);
    };

    return (
        <HrnbCard
            className={`post-card ${pinned ? 'post-card-pinned' : ''} ${hasUnread ? 'post-card-unread' : ''}`}
            onClick={handleViewDetails}
        >
            <div className="post-card-header">
                <div className="post-card-title-row">
                    <h3 className="post-card-title">{renderTitle()}</h3>
                    {pinned && <span className="post-card-pinned-badge" title="Закреплён">📌</span>}
                </div>
                {category && (
                    <span className="post-card-category">
                        {typeof category === 'string' ? category : (category.name || 'Категория')}
                    </span>
                )}
            </div>

            <div className="post-card-body">
                {content && (
                    <div className="post-card-content">
                        {renderContent(content)}
                    </div>
                )}

                {thumbnail && (
                    <div className="post-card-thumbnail">
                        <img
                            src={`${BASE_URL}/files/${thumbnail.id}/download`}
                            alt={thumbnail.fileName || title}
                            className="post-card-thumbnail-img"
                            loading="lazy"
                        />
                        {extraImageCount > 0 && (
                            <span className="post-card-thumbnail-badge">
                                +{extraImageCount}
                            </span>
                        )}
                    </div>
                )}

                <div className="post-card-meta">
                    {author && (
                        <span className="post-card-author">
                            Автор:{' '}
                            {typeof author === 'string'
                                ? author
                                : (author.id
                                    ? <Link to={`/profile/${author.id}`} className="post-card-author-link" onClick={(e) => e.stopPropagation()}>{author.displayName || author.username || author.name || 'Аноним'}</Link>
                                    : (author.displayName || author.username || author.name || 'Аноним'))}
                        </span>
                    )}
                    {createdAt && (
                        <span className="post-card-date">
                            {isEdited ? (
                                <>
                                    {formatDate(createdAt)} · ✎ обновлено {formatDate(updatedAt)}
                                </>
                            ) : (
                                formatDate(createdAt)
                            )}
                        </span>
                    )}
                </div>

                {tags && tags.length > 0 && (
                    <div className="post-card-tags">
                        {(tags || []).slice(0, 3).map((tag, index) => (
                            <TagBadge
                                key={tag.id || index}
                                tag={tag}
                                variant="default"
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="post-card-footer">
                <div className="post-card-stats">
                    <span className="post-card-stat">
                        👁 {displayViewCount}
                    </span>
                    <span className="post-card-stat">
                        💬 {displayCommentsCount}
                        {hasUnread && (
                            <span className="post-card-unread-badge" title={`${unreadCount} новых комментариев`}>
                                {unreadCount}
                            </span>
                        )}
                    </span>
                    <span
                        className={`post-card-stat post-card-like-clickable ${liked ? 'post-card-liked' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleLike(e);
                        }}
                        title={currentUser ? 'Нажмите для лайка' : 'Войдите, чтобы поставить лайк'}
                    >
                        👍 {likeCount}
                    </span>
                    <span
                        className={`post-card-stat post-card-dislike-clickable ${disliked ? 'post-card-disliked' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDislike(e);
                        }}
                        title={currentUser ? 'Нажмите для дизлайка' : 'Войдите, чтобы поставить дизлайк'}
                    >
                        👎 {dislikeCount}
                    </span>
                    {currentUser && (
                        <span
                            className={`post-card-stat post-card-subscribe-clickable ${subscribed ? 'post-card-subscribed' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!subscribeLoading) handleSubscribe(e);
                            }}
                            title={subscribed ? 'Отписаться от поста' : 'Подписаться на пост'}
                        >
                            {subscribeLoading ? '⏳' : (subscribed ? '🔔' : '🛎️')}
                        </span>
                    )}
                    {canPinPost() && (
                        <span
                            className="post-card-stat post-card-pin-clickable"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!pinLoading) handlePinToggle(e);
                            }}
                            title={pinned ? 'Открепить пост' : 'Закрепить пост'}
                        >
                            {pinLoading ? '⏳' : (pinned ? '📌' : '📍')}
                        </span>
                    )}
                    {canDeletePost() && (
                        <span
                            className="post-card-stat post-card-delete-clickable"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDeleteModalOpen(true);
                            }}
                            title="Удалить пост"
                        >
                            🗑️
                        </span>
                    )}
                </div>
            </div>

            {/* Модальное окно подтверждения удаления поста */}
            <HrnbModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Удаление поста"
                size="small"
            >
                <div className="delete-modal-content">
                    <p className="delete-modal-text">
                        Вы уверены, что хотите удалить пост «{title}»? Это действие нельзя отменить.
                    </p>
                    {error && <HrnbAlert variant="danger" onClose={() => setError(null)}>{error}</HrnbAlert>}
                    <div className="delete-modal-actions">
                        <HrnbButton
                            variant="outline"
                            onClick={() => setDeleteModalOpen(false)}
                            disabled={deleting}
                        >
                            Отмена
                        </HrnbButton>
                        <HrnbButton
                            variant="danger"
                            onClick={handleDeletePost}
                            disabled={deleting}
                        >
                            {deleting ? <HrnbLoader size="sm" /> : 'Удалить'}
                        </HrnbButton>
                    </div>
                </div>
            </HrnbModal>
        </HrnbCard>
    );
}

export default React.memo(PostCard, (prevProps, nextProps) => {
    return prevProps.post === nextProps.post
        && prevProps.onDelete === nextProps.onDelete
        && prevProps.onPinToggle === nextProps.onPinToggle
        && prevProps.searchQuery === nextProps.searchQuery
        && prevProps.isSubscribedView === nextProps.isSubscribedView;
});
